<template>
  <header class="Header" :class="{ 'Header--menuOpen': isMobileMenuOpen }">
    <div class="Header_Bar">
      <router-link to="/" class="Header_Brand">
        <img :src="logoUrl" :alt="translations.fr.logoAlt" class="Header_Logo" width="120" height="65" />
      </router-link>

      <nav class="Header_Nav" :aria-label="translations.fr.mainNavigation">
        <ul class="Header_Menu">
          <li>
            <router-link to="/a-propos" class="Header_MenuItem">
              {{ translations.fr.about }}
            </router-link>
          </li>
          <li ref="dropdown" class="Header_Dropdown" @keydown.esc="closeDropdown">
            <button type="button" class="Header_MenuItem" aria-haspopup="true" aria-controls="usages-menu"
              :aria-expanded="isDropdownOpen" @click="toggleDropdown">
              {{ translations.fr.usages }}
              <img :src="chevronUrl" alt="" class="Header_Chevron" width="16" height="16" />
            </button>
            <ul v-show="isDropdownOpen" id="usages-menu" class="Header_Submenu">
              <li v-for="item in usagesItems" :key="item.hash">
                <router-link :to="{ path: '/fonctionnalites', hash: item.hash }" class="Header_SubmenuItem">
                  {{ item.label }}
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
        <router-link to="/offres-de-spectacles" class="Header_Btn Header_Btn--outline">
          {{ translations.fr.showsDirectory }}
        </router-link>
        <a class="Header_Btn Header_Btn--white" :href="connectionUrl" target="_blank">
          {{ translations.fr.connexion }}
        </a>
      </nav>

      <div class="Header_MobileActions">
        <a class="Header_Btn Header_Btn--white" :href="connectionUrl" target="_blank">
          {{ translations.fr.connexion }}
        </a>
        <button type="button" class="Header_MenuToggle" aria-controls="mobile-menu" :aria-expanded="isMobileMenuOpen"
          :aria-label="isMobileMenuOpen ? translations.fr.closeMenu : translations.fr.openMenu" @click="toggleMobileMenu">
          <img v-if="isMobileMenuOpen" :src="closeIconUrl" alt="" width="11" height="11" />
          <img v-else :src="hamburgerIconUrl" alt="" width="16" height="10" />
        </button>
      </div>
    </div>

    <div v-if="pageTitle && !isMobileMenuOpen" class="Header_Hero"
      :class="{ 'Header_Hero--withTicker': route.meta.showTicker }">
      <component :is="route.meta.pageTitleIsHeading === false ? 'p' : 'h1'" class="Header_Title">
        {{ pageTitle }}
      </component>
    </div>

    <HomeTickerComponent v-if="route.meta.showTicker && !isMobileMenuOpen" />

    <nav v-if="isMobileMenuOpen" id="mobile-menu" class="Header_MobileMenu" :aria-label="translations.fr.mainNavigation">
      <ul>
        <li class="Header_MobileItem">
          <router-link to="/a-propos" class="Header_MobileLink">{{ translations.fr.about }}</router-link>
        </li>
        <li class="Header_MobileItem Header_MobileItem--group">
          <span class="Header_MobileLink">{{ translations.fr.usages }}</span>
          <ul class="Header_MobileSubmenu">
            <li v-for="item in usagesItems" :key="item.hash">
              <router-link :to="{ path: '/fonctionnalites', hash: item.hash }" class="Header_MobileSublink">
                {{ item.label }}
              </router-link>
            </li>
          </ul>
        </li>
        <li class="Header_MobileItem">
          <router-link to="/offres-de-spectacles" class="Header_MobileLink">
            {{ translations.fr.showsDirectory }}
          </router-link>
        </li>
      </ul>
    </nav>
  </header>
</template>

<script lang="ts" setup>

import translations from '@/dictionaries/navigation.json';
import logoUrl from '@/assets/img/logo-scene-pro-blanc.svg';
import chevronUrl from '@/assets/img/chevron-blanc.svg';
import hamburgerIconUrl from '@/assets/img/icon-hamburger-blanc.svg';
import closeIconUrl from '@/assets/img/icon-close-blanc.svg';
import HomeTickerComponent from '@/components/home/HomeTickerComponent.vue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const connectionUrl = process.env.VUE_APP_LOGIN_URL;
const route = useRoute();

// Every page shares the same gradient banner; only the title changes (set in route meta)
const pageTitle = computed(() => route.meta.pageTitle);

// Anchors on the features page until the dedicated "Usages" page exists
const usagesItems = [
  { hash: '#artistes-producteurs', label: translations.fr.usagesMenu.artistsProducers },
  { hash: '#diffuseurs', label: translations.fr.usagesMenu.presenters },
  { hash: '#reseaux', label: translations.fr.usagesMenu.networks },
  { hash: '#evenements-associations', label: translations.fr.usagesMenu.eventsAssociations },
  { hash: '#equipes-numeriques', label: translations.fr.usagesMenu.digitalTeams },
];

const dropdown = ref<HTMLElement | null>(null);
const isDropdownOpen = ref(false);
const isMobileMenuOpen = ref(false);
const desktopQuery = window.matchMedia('(min-width: 992px)');

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMenus = () => {
  closeDropdown();
  isMobileMenuOpen.value = false;
};

const closeDropdownOnOutsideClick = (event: MouseEvent) => {
  if (dropdown.value && !dropdown.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

const closeMobileMenuOnEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isMobileMenuOpen.value = false;
  }
};

watch(() => route.fullPath, closeMenus);

// The mobile menu covers the page, so the page behind must not scroll
watch(isMobileMenuOpen, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : '';
});

onMounted(() => {
  document.addEventListener('click', closeDropdownOnOutsideClick);
  document.addEventListener('keydown', closeMobileMenuOnEscape);
  desktopQuery.addEventListener('change', closeMenus);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdownOnOutsideClick);
  document.removeEventListener('keydown', closeMobileMenuOnEscape);
  desktopQuery.removeEventListener('change', closeMenus);
  document.body.style.overflow = '';
});

</script>

<style lang="scss" scoped src="./TheHeaderComponent.scss"></style>
